<script lang="ts" setup>
import type { CategoryGroupType, TaskType } from '~/types/tasks';

const props = defineProps<{
  task?: TaskType | null;
  isNotFound?: boolean;
  categories?: CategoryGroupType[] | null;
  withTags?: boolean;
}>();
const emit = defineEmits<{
  (event: 'react', reaction: 'like' | 'dislike'): void;
}>();

const mainStore = useMainStore();
const { t } = useI18n();

const feedbackVisible = ref(false);
</script>

<template>
  <GameSimpleCard class="sectionGamePlayCard" v-bind="props">
    <template #head>
      <Button @click="feedbackVisible = true" rounded text class="ml-auto" icon="pi pi-megaphone" />

      <DialogFeedback
        v-model:visible="feedbackVisible"
        type="task"
        :additional="t('feedback_context', { id: props.task?.id })"
      />
    </template>

    <template #timer v-if="task && !props.isNotFound">
      <GameTimer v-if="task.time" :time="task.time" :class="{ '-mt-8': task.media }" />

      <div
        v-if="mainStore.loaded"
        class="flex justify-between w-full max-w-64 px-4 -mb-2 md:-mb-4"
        :class="task.time ? '-mt-4' : 'mt-2 md:mt-4'"
      >
        <Button
          @click="emit('react', 'like')"
          text
          rounded
          size="large"
          :icon="`pi pi-thumbs-up${task.reaction === 'like' ? '-fill' : ''}`"
          :aria-label="t('like')"
        />
        <Button
          @click="emit('react', 'dislike')"
          text
          rounded
          size="large"
          :icon="`pi pi-thumbs-down${task.reaction === 'dislike' ? '-fill' : ''}`"
          :aria-label="t('dislike')"
        />
      </div>
    </template>
  </GameSimpleCard>
</template>

<i18n>
en:
  task_level: '{level} level'
  feedback_context: Task number {id}
  like: I like it.
  dislike: I don't like it.

ru:
  task_level: '{level} уровень'
  feedback_context: Задание номер {id}
  like: Нравится
  dislike: Не нравится

de:
  task_level: '{level} Ebene'
  feedback_context: Aufgabennummer {id}
  like: Gefällt mir.
  dislike: Das gefällt mir nicht.

es:
  task_level: '{level} nivel'
  feedback_context: Número de tarea {id}
  like: Me gusta.
  dislike: No me gusta.

fr:
  task_level: Niveau {level}
  feedback_context: Numéro de tâche {id}
  like: J'aime ça.
  dislike: Je n'aime pas ça.
</i18n>

<style lang="scss">
.sectionGamePlayCard {
  z-index: 2;
  position: relative;
  width: 100%;
  border-radius: 24px !important;
  max-width: 720px;
  margin: 0 auto;

  & .p-card-content {
    padding: 1rem;

    @media (max-width: 640px) {
      padding: 0.5rem;
    }
  }

  @media (max-width: 1024px) {
    & .p-card-body {
      padding: 1rem;
    }
  }
}
</style>
